import { useCallback } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';
import type { ADNode } from '@atlaskit/editor-common/validator';

import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';

import { useGetCollabProviderState } from './useGetCollabProviderState.ts';

export const RETRIEVE_EDITOR_TITLE_AND_CONTENT_ERROR = {
	COLLAB_PROVIDER: 'RETRIEVE_EDITOR_TITLE_AND_CONTENT_ERROR_COLLAB_PROVIDER',
	ACK_STATE: 'RETRIEVE_EDITOR_TITLE_AND_CONTENT_ERROR_ACK_STATE',
};

export type RetrieveEditorTitleAndContent = () => Promise<{
	title?: string | null;
	adf: ADNode;
	syncRev?: string;
	ncsStepVersion?: string;
}>;

type UseRetrieveEditorTitleAndContentProps = {
	onCollabError: () => void;
	editorActions: EditorActions;
};

export const useRetrieveEditorTitleAndContent = ({
	onCollabError,
	editorActions,
}: UseRetrieveEditorTitleAndContentProps) => {
	const [{ collabEditProvider }] = useNativeCollabState();
	const getCollabProviderState = useGetCollabProviderState({
		editorActions,
		collabEditProvider,
		reason: 'get-content',
	});

	const retrieveEditorTitleAndContent = useCallback(() => {
		return new Promise<{
			adf: {
				[key: string]: any;
				type: '';
			};
			title?: string;
			ncsStepVersion: string;
		}>((resolve, reject) => {
			if (!collabEditProvider) {
				return reject(RETRIEVE_EDITOR_TITLE_AND_CONTENT_ERROR.COLLAB_PROVIDER);
			}

			return Promise.resolve(getCollabProviderState(true))
				.then((ackState) => {
					if (!ackState) {
						return reject(RETRIEVE_EDITOR_TITLE_AND_CONTENT_ERROR.ACK_STATE);
					}

					const { title, content, stepVersion } = ackState;

					return resolve({
						title: title as string,
						adf: content,
						ncsStepVersion: stepVersion.toString(),
					});
				})
				.catch((error) => {
					onCollabError();
					reject(error);
				});
		});
	}, [onCollabError, getCollabProviderState, collabEditProvider]);

	return {
		retrieveEditorTitleAndContent,
	};
};
